import React from 'react'
import { calculateUrgency, URGENCY_GREEN, URGENCY_AMBER, URGENCY_RED } from '../utils/urgencyColors'
import '../styles/updateanaddform.css'

interface DueDateProgressProps {
  createdAt: string | Date
  dueDate: string | Date
  status: string
  theme?: string
}

/**
 * Builds a full-width gradient where each color gets an equal share
 * of dominance across the urgency range:
 *
 *   urgency 0–33  → green is majority  (tile = green)
 *   urgency 34–67 → amber is majority  (tile = amber)
 *   urgency 68–100 → red is majority   (tile = red)
 *
 * Color proportions:
 *   greenPortion = max(0, 100 - urgency × 1.5)
 *   redPortion   = max(0, (urgency - 33.3) × 1.5)
 *   amberPortion = remainder
 */
function buildUrgencyGradient(urgency: number): string {
  const greenEnd = Math.max(0, 100 - urgency * 1.5)
  const redStart = Math.min(100, 100 - Math.max(0, (urgency - 33.33) * 1.5))

  const BLEND = 4

  const stops: string[] = []

  if (greenEnd > 0) {
    stops.push(`${URGENCY_GREEN} 0%`, `${URGENCY_GREEN} ${Math.max(0, greenEnd - BLEND)}%`)
  }

  if (redStart > greenEnd) {
    stops.push(
      `${URGENCY_AMBER} ${Math.min(100, greenEnd + BLEND)}%`,
      `${URGENCY_AMBER} ${Math.max(0, redStart - BLEND)}%`
    )
  }

  if (redStart < 100) {
    stops.push(`${URGENCY_RED} ${Math.min(100, redStart + BLEND)}%`, `${URGENCY_RED} 100%`)
  }

  if (stops.length === 0) return URGENCY_RED

  return `linear-gradient(to right, ${stops.join(', ')})`
}

const DueDateProgress: React.FC<DueDateProgressProps> = ({
  dueDate,
  status,
  theme,
}) => {
  if (status === 'done') return null

  const urgency = calculateUrgency(dueDate)

  return (
    <div className={`due-date-progress${theme ? ' due-date-progress--bordered' : ''}`}>
      <div
        className="due-date-progress__fill"
        style={{ background: buildUrgencyGradient(urgency) }}
      />
    </div>
  )
}

export default DueDateProgress
